import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Satellite, RefreshCw } from "lucide-react";

/**
 * Lee el kilometraje real del cuadro desde el GPS (Traccar) y lo vuelca en el
 * campo de km del formulario. El campo sigue siendo editable: si la lectura es
 * antigua o el motor está apagado, se avisa y se puede corregir a mano.
 *
 * - Sin `code`: usa el endpoint de administración de la reserva
 *   (/reservations/{reservationId}/telemetry/odometer), que lee el GPS de su vehículo.
 * - Con `code`: usa el endpoint público de la reserva (/r/{code}/telemetry/odometer).
 * - `onValue(km)` se llama al obtener la lectura; `auto` rellena nada más montar
 *   el componente (solo si `current` está vacío).
 */
export default function GpsOdometer({ code, reservationId, onValue, onFuel, current, auto = true, testId = "gps-km" }) {
  const [state, setState] = useState({ loading: false, reading: null, error: null });

  const read = async (silent = false) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const url = code ? `/r/${code}/telemetry/odometer` : `/reservations/${reservationId}/telemetry/odometer`;
      const { data } = await api.get(url);
      setState({ loading: false, reading: data, error: null });
      onValue?.(data.km);
      // Combustible: el % OBD sigue al indicador del cuadro (verificado en el T7 el
      // 09/10/2026: 100 % los primeros ~250 km tras llenar, luego baja). Se propone
      // el tramo (lleno/3/4/1/2/1/4/reserva) y el propietario lo corrige si no cuadra.
      if (data.fuel_level) onFuel?.(data.fuel_level);
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 503 ? null : // GPS no configurado: no molestar
        e?.response?.data?.detail || "No se ha podido leer el GPS";
      setState({ loading: false, reading: null, error: silent && status === 503 ? null : msg });
    }
  };

  useEffect(() => {
    if (auto && (current === "" || current == null)) read(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loading, reading, error } = state;
  const stale = reading && reading.age_minutes > 60;

  return (
    <div className="mt-1 flex items-center gap-2 text-xs text-[#8A8A8A]" data-testid={testId}>
      <Button type="button" size="sm" variant="outline" onClick={() => read(false)} disabled={loading} className="h-7 px-2 text-xs">
        {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Satellite className="w-3 h-3" />}
        <span className="ml-1">{loading ? "Leyendo…" : "Leer del GPS"}</span>
      </Button>
      {reading && (
        <span className={stale ? "text-amber-600" : ""}>
          {reading.km.toLocaleString("es-ES")} km según el GPS
          {reading.age_minutes < 2 ? " (ahora)" : ` (hace ${reading.age_minutes < 120 ? `${reading.age_minutes} min` : `${Math.round(reading.age_minutes / 60)} h`})`}
          {stale ? " · comprueba el cuadro" : ""}
          {reading.fuel_liters != null
            ? ` · combustible ${reading.fuel_liters} L`
            : reading.fuel_percent != null
              ? ` · combustible ${reading.fuel_percent} % (${reading.fuel_level})`
              : ""}
        </span>
      )}
      {error && <span className="text-red-600">{error}</span>}
    </div>
  );
}
